<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  companyId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const conversations = ref([]);

const hasConversations = computed(() => conversations.value.length > 0);

const fetchCompanyConversations = async () => {
  isLoading.value = true;
  try {
    // Fetch conversations for all contacts in this company
    // We'll get the company details first which includes contacts
    const company = store.getters['companies/getSelectedCompany'];
    if (!company?.contacts?.length) {
      conversations.value = [];
      return;
    }

    // Fetch conversations for each contact
    const conversationPromises = company.contacts.map(contact => 
      store.dispatch('contactConversations/get', contact.id)
        .catch(() => []) // Ignore errors for individual contacts
    );

    const results = await Promise.all(conversationPromises);
    
    // Flatten and sort by last activity
    const allConversations = results
      .flat()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    conversations.value = allConversations.slice(0, 50); // Limit to 50 most recent
  } catch (error) {
    conversations.value = [];
  } finally {
    isLoading.value = false;
  }
};

const getContactName = (contactId) => {
  const company = store.getters['companies/getSelectedCompany'];
  const contact = company?.contacts?.find(c => c.id === contactId);
  return contact?.name || t('COMPANIES.CONTACTS.UNNAMED');
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : new Date(timestamp);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
};

const getStatusColor = (status) => {
  const colors = {
    open: 'bg-n-green-9',
    resolved: 'bg-n-slate-9',
    pending: 'bg-n-amber-9',
    snoozed: 'bg-n-blue-9',
  };
  return colors[status] || 'bg-n-slate-9';
};

// Fetch when companyId changes
watch(() => props.companyId, (newId) => {
  if (newId) {
    fetchCompanyConversations();
  }
}, { immediate: true });
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="flex-1 overflow-y-auto px-6 py-6">
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      
      <div v-else-if="hasConversations" class="flex flex-col gap-4">
        <div
          v-for="conversation in conversations"
          :key="conversation.id"
          class="flex gap-3 p-3 bg-n-alpha-black2 rounded-lg hover:bg-n-alpha-black3 transition-colors cursor-pointer"
          @click="$router.push(`/app/accounts/${$route.params.accountId}/conversations/${conversation.id}`)"
        >
          <Avatar
            :src="conversation.meta?.sender?.thumbnail || ''"
            :name="getContactName(conversation.meta?.sender?.id)"
            :size="32"
            hide-offline-status
            rounded-full
          />
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ getContactName(conversation.meta?.sender?.id) }}
              </span>
              <span class="text-xs text-n-slate-11 flex-shrink-0">
                {{ formatTime(conversation.created_at) }}
              </span>
            </div>
            <div class="flex items-center gap-2 mt-1">
              <span
                class="w-2 h-2 rounded-full"
                :class="getStatusColor(conversation.status)"
              />
              <span class="text-xs text-n-slate-11 capitalize">
                {{ conversation.status }}
              </span>
              <span class="text-xs text-n-slate-10">
                #{{ conversation.id }}
              </span>
            </div>
            <p
              v-if="conversation.last_non_activity_message?.content"
              class="text-sm text-n-slate-11 mt-2 truncate"
            >
              {{ conversation.last_non_activity_message.content }}
            </p>
          </div>
        </div>
      </div>
      
      <div v-else class="flex flex-col items-center justify-center py-10 text-center">
        <span class="i-lucide-history text-n-slate-9 size-12 mb-4" />
        <h4 class="text-base font-medium text-n-slate-12 mb-2">
          {{ t('COMPANIES.HISTORY.TITLE') }}
        </h4>
        <p class="text-sm text-n-slate-11 max-w-xs">
          {{ t('COMPANIES.HISTORY.EMPTY_STATE') }}
        </p>
        <p class="text-xs text-n-slate-10 mt-4">
          {{ t('COMPANIES.HISTORY.START_CONVERSATIONS') }}
        </p>
      </div>
    </div>
  </div>
</template>
